import { clsx } from 'clsx';
import type { SkillName } from '@character-creator/types';
import { formatSkill } from '../../utils/formatting';
import { getExpertiseAllowed } from './expertiseUtils';

interface ExpertiseSelectorProps {
  classId: string;
  level: number;
  proficientSkills: SkillName[];
  selectedExpertise: SkillName[];
  onToggle: (skill: SkillName) => void;
  compact?: boolean;
}

export function ExpertiseSelector({
  classId,
  level,
  proficientSkills,
  selectedExpertise,
  onToggle,
  compact = false,
}: ExpertiseSelectorProps) {
  const maxAllowed = getExpertiseAllowed(classId, level);
  const currentCount = selectedExpertise.length;
  const canSelectMore = currentCount < maxAllowed;
  const needsSelection = currentCount < maxAllowed;

  // Compact view - shows selected expertise with change option
  if (compact && !needsSelection) {
    return (
      <div className="text-xs text-success">
        <span>Expertise: </span>
        {selectedExpertise.map((skill) => formatSkill(skill)).join(', ')}
      </div>
    );
  }

  return (
    <div>
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Expertise
        {needsSelection && (
          <span className="text-warning ml-2 text-xs">
            (Choose {maxAllowed - currentCount} more)
          </span>
        )}
        {!needsSelection && (
          <span className="text-success ml-2 text-xs">
            ({currentCount}/{maxAllowed} selected)
          </span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-3">
        Choose skills you're proficient in to gain expertise (double proficiency bonus).
      </p>
      <div className="grid grid-cols-2 gap-2">
        {proficientSkills.map((skill) => {
          const isSelected = selectedExpertise.includes(skill);
          const isDisabled = !isSelected && !canSelectMore;

          return (
            <button
              key={skill}
              type="button"
              onClick={() => !isDisabled && onToggle(skill)}
              disabled={isDisabled}
              className={clsx(
                'px-3 py-2 text-sm rounded-lg border transition-all duration-200 text-left',
                isSelected
                  ? 'bg-accent-gold/10 border-accent-gold text-text-primary'
                  : isDisabled
                    ? 'bg-background-secondary border-background-elevated text-text-muted cursor-not-allowed opacity-50'
                    : 'bg-background-secondary border-background-elevated text-text-secondary hover:border-text-muted'
              )}
            >
              {formatSkill(skill)}
              {isSelected && <span className="ml-1 text-accent-gold">★</span>}
            </button>
          );
        })}
      </div>
      {proficientSkills.length === 0 && (
        <p className="text-xs text-text-muted italic">
          No skill proficiencies available. Select skills first.
        </p>
      )}
    </div>
  );
}
